import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import {
  Button,
  LockIcon,
  Modal,
  NewTag,
  useDismissedHint,
} from "../../../components/ui";
import { BRAND_CONFIGURATION_PATH, ROUTES } from "../../../routes/paths";

/**
 * Which dishes the Dish field searches: the whole catalog, or only the
 * workspace's own — the rows `FoodOption.isOwn` marks.
 */
export type DishScope = "all" | "mine";

export interface DishScopeToggleProps {
  scope: DishScope;
  onChange: (scope: DishScope) => void;
  /**
   * Whether the padlock may be shut at all — only once the workspace has a
   * dish of its own, since a search kept to none finds nothing. `undefined`
   * while that is still being found out, which holds the padlock too. Opening
   * it is never gated.
   */
  canLock: boolean | undefined;
}

/**
 * The control itself: "My dishes" with a padlock beside it. Shut, on a brand
 * tint, when the search is kept to your own dishes; open, on white, when the
 * Naurist catalog is let in too. Pressing the padlock never flips the setting
 * by itself — it asks first, since the answer is kept across every plan.
 */
const ScopeLock: React.FC<{
  locked: boolean;
  /** Whether pressing it does anything — see `DishScopeToggleProps.canLock`. */
  disabled: boolean;
  onPress: () => void;
}> = ({ locked, disabled, onPress }) => (
  <div
    className={`inline-flex items-center gap-3 rounded-[6px] border px-3 py-2 transition-colors ${
      locked ? "border-brand/30 bg-brand-soft" : "border-line bg-white"
    } ${disabled ? "opacity-60" : ""}`}
  >
    <span className="text-[14px] 3xl:text-[15px] font-medium text-ink">
      My dishes
    </span>
    <button
      type="button"
      aria-label={locked ? "Unlock dish search" : "Lock dish search"}
      aria-pressed={locked}
      disabled={disabled}
      title={disabled ? "Add a dish of your own first" : undefined}
      onClick={onPress}
      className={`flex h-6 w-6 items-center justify-center rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed ${
        locked
          ? "text-brand-dark hover:text-brand"
          : "text-ink-subtle enabled:hover:text-ink"
      }`}
    >
      <LockIcon open={!locked} className="h-5 w-5" />
    </button>
  </div>
);

/**
 * The question asked before the padlock turns, either way. What it says
 * depends on which way it is turning: unlocking widens the search to the
 * Naurist catalog, locking narrows it back to your own dishes. The one thing
 * both say is that the answer holds for every plan, not just this one.
 */
const ScopeConfirm: React.FC<{
  open: boolean;
  locked: boolean;
  onKeep: () => void;
  onTurn: () => void;
}> = ({ open, locked, onKeep, onTurn }) => (
  <Modal
    open={open}
    title={locked ? "Unlock dish search?" : "Lock dish search?"}
    onClose={onKeep}
    className="max-w-[500px]"
    footer={
      <>
        <Button variant="secondary" size="md" onClick={onKeep}>
          {locked ? "Keep locked" : "Keep unlocked"}
        </Button>
        <Button size="md" onClick={onTurn}>
          {locked ? "Unlock" : "Lock"}
        </Button>
      </>
    }
  >
    <p className="text-[14px] 3xl:text-[15px] leading-relaxed text-ink-muted">
      {locked ? (
        <>
          Right now only{" "}
          <span className="font-medium text-ink">your dishes</span> show when
          you search. Unlocking lets the full Naurist catalog appear too.
        </>
      ) : (
        <>
          Right now the{" "}
          <span className="font-medium text-ink">full Naurist catalog</span>{" "}
          shows when you search. Locking keeps it to your own dishes only.
        </>
      )}
    </p>
    <p className="mt-4 rounded-[6px] border border-line bg-[#F7F9FA] px-4 py-3 text-[13px] 3xl:text-[14px] text-ink-muted">
      This applies to every meal plan, not just this one &ndash; until you{" "}
      {locked ? "lock" : "unlock"} it again.
    </p>
  </Modal>
);

/**
 * The card that introduces the padlock, hung under it with a caret pointing
 * up at what it is about. Dark, so it reads as a note laid over the page
 * rather than as another part of it.
 *
 * Shown the first time a plan is opened in this browser and then never again
 * — only "Okay, got it" puts it away, and that is remembered. Clicking
 * elsewhere on the page leaves it up: it is short, and it points at a
 * setting worth reading about once.
 */
const ScopeIntro: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => (
  // A region rather than a dialog: nothing waits on it, and the page's real
  // dialogs — the meal and send ones — are the only things that should answer
  // to that name.
  <section
    aria-labelledby="dish-scope-intro-title"
    className="absolute right-0 top-full z-20 mt-3 w-[294px] rounded-[8px] bg-[#00424B] p-4 text-white shadow-[0px_8px_24px_0px_#00000029]"
  >
    {/* The caret, sat under the control's own centre rather than the card's:
        the card is wider than what it points at and right-aligned to it. */}
    <span
      aria-hidden="true"
      className="absolute -top-2 right-[56px] h-4 w-4 rotate-45 rounded-[2px] bg-[#00424B]"
    />
    <NewTag>New</NewTag>
    <h2
      id="dish-scope-intro-title"
      className="mt-2 text-[15px] font-medium leading-snug"
    >
      Choose your dish search
    </h2>
    <p className="mt-1.5 text-[12px] leading-relaxed text-white/85">
      Search from <span className="font-medium text-white">your dishes</span>,
      or expand your search to{" "}
      <span className="font-medium text-white">
        include dishes available in the Naurist library
      </span>
      .
    </p>
    <div className="mt-4 flex items-center justify-between gap-3">
      {/* Where the setting is explained — the same tab the line under the
          control points at. */}
      <Link
        to={BRAND_CONFIGURATION_PATH}
        className="rounded text-[13px] font-medium text-brand hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
      >
        Learn more in Settings
      </Link>
      <Button size="sm" onClick={onDismiss}>
        Okay, got it
      </Button>
    </div>
  </section>
);

const linkClasses =
  "rounded font-medium text-brand hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40";

/**
 * The Dish search padlock as the plan header carries it, with a line under
 * it where there is something to say: while locked, that the setting holds
 * for every plan and the way out — straight to Unlock, since that is the one
 * change worth a shortcut; while it cannot be locked, why, and where to put
 * that right. An open padlock that could be shut says nothing — the padlock
 * is the whole message.
 */
const DishScopeToggle: React.FC<DishScopeToggleProps> = ({
  scope,
  onChange,
  canLock,
}) => {
  const [introduced, dismissIntro] = useDismissedHint("dish-scope-intro");
  const [confirming, setConfirming] = useState(false);
  const locked = scope === "mine";
  // Opening is always allowed; shutting waits on a dish to shut it around.
  const held = !locked && canLock !== true;

  const turn = () => {
    setConfirming(false);
    onChange(locked ? "all" : "mine");
  };

  return (
    <div className="relative flex flex-col items-end gap-1">
      <ScopeLock
        locked={locked}
        disabled={held}
        onPress={() => setConfirming(true)}
      />
      {locked && (
        <p className="flex items-center gap-1 text-[11px] 3xl:text-[12px] text-ink-muted">
          <Info className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
          Applies to every meal plan ·{" "}
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className={linkClasses}
          >
            Unlock
          </button>
        </p>
      )}
      {!locked && canLock === false && (
        <p className="flex items-center gap-1 text-[11px] 3xl:text-[12px] text-ink-muted">
          <Info className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
          Add a dish of your own to lock this ·{" "}
          <Link to={ROUTES.dishes} className={linkClasses}>
            Dishes
          </Link>
        </p>
      )}

      {!introduced && <ScopeIntro onDismiss={dismissIntro} />}

      <ScopeConfirm
        open={confirming}
        locked={locked}
        onKeep={() => setConfirming(false)}
        onTurn={turn}
      />
    </div>
  );
};

export default DishScopeToggle;
